/**
 * Atlas — Reusable data hooks.
 */
import { useState, useEffect, useCallback } from 'react';
import { propertyAPI, timelineAPI, marketplaceAPI } from '../services/api';

// ── useProperty ──
export function useProperty(propertyId) {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await propertyAPI.get(propertyId);
      setProperty(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load property');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { property, loading, error, refetch: fetch };
}

// ── useProperties (list) ──
export function useProperties(page = 1) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await propertyAPI.list(page);
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetch(); }, [fetch]);

  return { ...data, loading, refetch: fetch };
}

// ── useTimeline ──
export function useTimeline(propertyId, filters = {}) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await timelineAPI.list(propertyId, filters);
      setEntries(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);

  return { entries, total, loading, refetch: fetch };
}

// ── usePros ──
export function usePros(params = {}) {
  const [pros, setPros] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await marketplaceAPI.searchPros(params);
      setPros(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]);

  useEffect(() => { fetch(); }, [fetch]);

  return { pros, total, loading, refetch: fetch };
}

// ── useBookings ──
export function useBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await marketplaceAPI.listBookings();
      setBookings(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { bookings, loading, refetch: fetch };
}

// ── useBookingMessages ──
export function useBookingMessages(bookingId) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const { data } = await marketplaceAPI.listMessages(bookingId);
      setMessages(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetch(); }, [fetch]);

  // Poll for new messages every 8 seconds while a booking is open
  useEffect(() => {
    if (!bookingId) return;
    const t = setInterval(fetch, 8000);
    return () => clearInterval(t);
  }, [fetch, bookingId]);

  return { messages, loading, refetch: fetch };
}
